const { getElement } = window.BAO.Utils

const QUERY = `
  query VariantInventory($productHandle: String!) {
    product(handle: $productHandle) {
      variants (first: 100) {
        nodes {
          id
          quantityAvailable
        }
      }
    }
  }
`

class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

export class ProductUrgencyMessage extends window.BAO.CustomElement() {
  static get observedAttributes () {
    return ['data-variant-id']
  }

  static get requiredAttributes () {
    return ['data-product-handle', 'data-variant-id']
  }

  constructor () {
    super()

    this.threshold = parseFloat(this.dataset.threshold)

    // Wrapper including the icon and the message text.
    this.message = getElement(
      `[data-variant-message][data-product-id="${this.dataset.productId}"]`,
    )

    this.noStockMessage = getElement(
      `[data-product-id="${this.dataset.productId}"] [data-variant-message-oos]`,
    )
    this.messageText = getElement(
      `[data-product-id="${this.dataset.productId}"] [data-variant-message-text]`,
    )

    this.isBundle = this.dataset.isBundle === 'true'

    this.selectionMade = false
    this.cache = new Cache()
  }

  connectedCallback () {
    super.connectedCallback()

    this.getProductData().then(data => {
      this.log('getProductData', { data })

      this.data = data

      // Uncomment below lines if you want to show the message on page load.
      // const currentVariantQuantity = this.getVariantQuantity(
      //   data,
      //   this.getVariantId(this.dataset.variantId),
      // )
      //
      // if (currentVariantQuantity === undefined) return
      // this.showMessage(parseFloat(currentVariantQuantity))
    })
  }

  attributeChangedCallback (name, oldValue, newValue) {
    this.log('attributeChangedCallback', { name, oldValue, newValue })

    switch (name) {
      case 'data-variant-id': {
        if (typeof this.data === 'undefined') {
          this.hideMessage()

          return
        }

        // If the selection is made and the value is the same as the previous one,
        // it means the selection has gone back to 'Please select' option, so we
        // can hide the message.
        if (this.selectionMade && oldValue === newValue) {
          this.hideMessage()

          return
        }

        const currentVariantQuantity = this.getVariantQuantity(
          this.data,
          this.getVariantId(newValue),
        )

        this.log('data-variant-id', {
          currentVariantQuantity,
          data: this.data,
          variantId: this.getVariantId(newValue),
        })

        if (currentVariantQuantity === undefined) return

        this.showMessage(parseFloat(currentVariantQuantity))
      }
    }

    this.selectionMade = true
  }

  async getProductData () {
    return this.makeStorefrontRequest({
      body: JSON.stringify({
        query: QUERY,
        variables: {
          productHandle: this.productHandle,
        },
      }),
    })
  }

  getVariantQuantity (data, variantId) {
    this.log('getVariantQuantity', { data, variantId })

    const variant = data.product.variants.nodes.find(
      variant => variant.id === variantId,
    )

    return variant?.quantityAvailable
  }

  showMessage (amount) {
    this.noStockMessage.setAttribute('aria-hidden', true)

    if (amount < 1) {
      this.noStockMessage.setAttribute('aria-hidden', false)
      this.message.setAttribute('aria-hidden', false)
      this.messageText.setAttribute('aria-hidden', true)
    } else {
      this.message.setAttribute('aria-hidden', amount > this.threshold)
      this.messageText.setAttribute('aria-hidden', false)

      this.textContent = amount.toString()
    }
  }

  hideMessage () {
    this.message.setAttribute('aria-hidden', true)
    this.messageText.setAttribute('aria-hidden', true)
    this.noStockMessage.setAttribute('aria-hidden', true)
  }

  /**
   * @param {RequestInit} options
   */
  async makeStorefrontRequest (options) {
    if (this.cache.exists(this.getCacheKey())) {
      return this.cache.get(this.getCacheKey())
    }

    try {
      const { data } = await window.BAO.Utils.fetchJSON(
        `/api/2024-01/graphql.json`,
        Object.assign(
          {},
          {
            method: 'POST',
            headers: {
              Accept: `application/json`,
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token':
                window.theme.storefrontAccessToken,
            },
          },
          options,
        ),
      )

      this.cache.set(this.getCacheKey(), data)

      return data
    } catch (e) {
      console.error(e)
    }
  }

  getCacheKey () {
    return `${this.dataset.productId}-${this.dataset.variantId}-${this.productHandle}`
  }

  getVariantId (variantId) {
    return `gid://shopify/ProductVariant/${variantId}`
  }

  get productHandle () {
    return this.dataset.productHandle
  }
}

if (!customElements.get('product-urgency-message')) {
  customElements.define('product-urgency-message', ProductUrgencyMessage)
}
